import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Bell, BookOpen, Menu, X, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const links = [
  { to: "/panel", label: "Panel" },
  { to: "/kitaplar", label: "Kitaplar" },
  { to: "/kesfet", label: "Keşfet" },
  { to: "/listeler", label: "Listeler" },
  { to: "/lider-tablosu", label: "Lider Tablosu" },
  { to: "/etkinlikler", label: "Etkinlikler" },
  { to: "/rozetler", label: "Rozetler" },
  { to: "/sozluk", label: "Sözlük" },
  { to: "/mesajlar", label: "Mesajlar" },
  { to: "/bildirimler", label: "Bildirimler" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState({ items: [], unread: 0 });
  const [mobileOpen, setMobileOpen] = useState(false);

  const loadNotifs = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch (e) {}
  }, []);

  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 30000);
    return () => clearInterval(t);
  }, [loadNotifs, location.pathname]);

  const markAll = async () => { await api.post("/notifications/read-all"); loadNotifs(); };
  const onNotifClick = async (n) => {
    await api.post(`/notifications/${n.id}/read`);
    loadNotifs();
    if (n.link) navigate(n.link);
  };

  const isStaff = ["admin", "moderator"].includes(user?.role);
  const active = (to) => location.pathname === to;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-page/80 border-b border-line">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-16">
        <Link to="/panel" className="flex items-center gap-2" data-testid="navbar-logo">
          <img src="/favicon.png" alt="AktifOkur" className="w-7 h-7 object-contain" />
          <span className="font-serif text-2xl text-ink tracking-tight">AktifOkur</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} data-testid={`nav-${l.label}`}
              className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${active(l.to) ? "text-terra" : "text-ink-2 hover:text-terra"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <button data-testid="notif-bell" className="relative p-2 rounded-full hover:bg-surface-alt transition-colors">
                <Bell size={20} className="text-ink-2" />
                {notifs.unread > 0 && (
                  <span className="absolute top-0.5 right-0.5 bg-terra text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">
                    {notifs.unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0 bg-surface border-line">
              <div className="flex items-center justify-between p-3 border-b border-line">
                <span className="font-medium text-ink">Bildirimler</span>
                {notifs.unread > 0 && <button onClick={markAll} className="text-xs text-terra hover:underline">Tümünü okundu işaretle</button>}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifs.items.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">Henüz bildirim yok.</p>}
                {notifs.items.map((n) => (
                  <button key={n.id} onClick={() => onNotifClick(n)} data-testid={`notif-${n.id}`}
                    className={`w-full text-left p-3 border-b border-line/60 hover:bg-surface-alt transition-colors ${!n.read ? "bg-terra/5" : ""}`}>
                    <p className="text-sm text-ink-2 leading-snug">{n.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">{new Date(n.created_at).toLocaleString("tr-TR")}</p>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="profile-menu" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-surface-alt transition-colors">
                <div className="w-8 h-8 rounded-full bg-terra/10 border border-line flex items-center justify-center overflow-hidden">
                  {user?.profil_resmi ? <img src={user.profil_resmi} alt="" className="w-full h-full object-cover" /> :
                    <span className="font-serif text-terra">{(user?.ad_soyad || "?")[0]}</span>}
                </div>
                <ChevronDown size={14} className="text-muted-foreground hidden sm:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52 bg-surface border-line">
              <div className="px-3 py-2">
                <p className="text-sm font-medium text-ink truncate">{user?.ad_soyad}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="menu-profil">Profilim</DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/onerilerim")} data-testid="menu-oneriler">Önerilerim</DropdownMenuItem>
              {isStaff && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Yönetim Paneli</DropdownMenuItem>}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/giris"); }} data-testid="menu-logout" className="text-destructive">Çıkış Yap</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button className="md:hidden p-2" onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-toggle">
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <nav className="md:hidden border-t border-line bg-page px-4 py-2">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)}
              className={`block px-3 py-2 rounded-md text-sm ${active(l.to) ? "text-terra" : "text-ink-2"}`}>{l.label}</Link>
          ))}
        </nav>
      )}
    </header>
  );
}
